import { useState, useRef, useCallback } from 'react';
import { deepClone } from '../utils/canvasUtils';

// Хук для управления историей и буфером обмена
export const useHistoryAndClipboard = () => {
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [showHistory, setShowHistory] = useState(false);
    const clipboardRef = useRef(null);

    // Референсы для отслеживания действий
    const dragInProgressRef = useRef(false);
    const lastActionTypeRef = useRef(null);
    const snapshotBeforeActionRef = useRef(null);
    const interactionActiveRef = useRef(false);

    const recordHistory = (elements, label = 'Изменение') => {
        // Проверяем, что elements существует и является массивом
        if (!elements || !Array.isArray(elements)) {
            return;
        }

        try {
            const entry = {
                id: Date.now() + Math.random(),
                timestamp: new Date().toLocaleTimeString(),
                label,
                elements: deepClone(elements)
            };

            setHistory((prev) => {
                // Удаляем все записи после текущего индекса (они станут недействительны)
                const newHistory = prev.slice(0, historyIndex + 1);
                newHistory.push(entry);
                return newHistory.slice(-300); // Храним последние 300 записей
            });

            setHistoryIndex((prev) => Math.min(prev + 1, history.length));
        } catch (error) {
            console.warn('Не удалось записать историю:', error);
        }
    };

    const snapshotBefore = (elements) => {
        if (!elements || !Array.isArray(elements)) {
            snapshotBeforeActionRef.current = [];
            return;
        }

        try {
            snapshotBeforeActionRef.current = deepClone(elements);
        } catch (error) {
            console.warn('Не удалось создать снимок:', error);
            snapshotBeforeActionRef.current = [];
        }
    };

    const copyElements = (elements, selectedElementIds, selectedElementId) => {
        if (!elements || !Array.isArray(elements)) {
            return;
        }

        const ids = selectedElementIds.size
            ? Array.from(selectedElementIds)
            : selectedElementId
                ? [selectedElementId]
                : [];

        try {
            clipboardRef.current = deepClone(
                elements.filter((el) => ids.includes(el.id))
            );
        } catch (error) {
            console.warn('Не удалось скопировать элементы:', error);
            clipboardRef.current = [];
        }
    };

    const pasteElements = () => {
        const src = clipboardRef.current || [];
        if (!src.length) return null;

        try {
            return src.map((el) => ({
                ...deepClone(el),
                id: Date.now() + Math.random(),
                x: (el.x || 0) + 20,
                y: (el.y || 0) + 20,
                selected: false
            }));
        } catch (error) {
            console.warn('Не удалось вставить элементы:', error);
            return null;
        }
    };

    const cutElements = (elements, selectedElementIds, selectedElementId, deleteSelectedElements) => {
        copyElements(elements, selectedElementIds, selectedElementId);
        deleteSelectedElements();
    };

    const undo = useCallback(() => {
        if (historyIndex > 0) {
            setHistoryIndex((prev) => prev - 1);
            return history[historyIndex - 1]?.elements || null;
        }
        return null;
    }, [historyIndex, history]);

    const redo = useCallback(() => {
        if (historyIndex < history.length - 1) {
            setHistoryIndex((prev) => prev + 1);
            return history[historyIndex + 1]?.elements || null;
        }
        return null;
    }, [historyIndex, history]);

    const startInteraction = () => {
        interactionActiveRef.current = true;
    };

    const endInteraction = (elements, label = 'Изменение') => {
        if (!interactionActiveRef.current) return;
        interactionActiveRef.current = false;
        if (!elements || !Array.isArray(elements)) return;
        recordHistory(elements, label);
    };

    const cancelInteraction = () => {
        interactionActiveRef.current = false;
    };

    const canUndo = historyIndex > 0;
    const canRedo = historyIndex < history.length - 1;

    return {
        history,
        historyIndex,
        showHistory,
        setShowHistory,
        dragInProgressRef,
        lastActionTypeRef,
        snapshotBeforeActionRef,
        recordHistory,
        snapshotBefore,
        copyElements,
        pasteElements,
        cutElements,
        clipboardRef,
        undo,
        redo,
        canUndo,
        canRedo,
        startInteraction,
        endInteraction,
        cancelInteraction
    };
};
